<template>
  <div>
    <slot
      name="button"
      :is-added-to-any-list="isAddedToAnyList"
      :lists="mentorAddedToListsNames"
      :open-modal="openAddMentorToListModal"
    />

    <BaseModal :name="addMentorToListModalName" width-class="tw-max-w-[250px]">
      <div>
        <div class="tw-p-3">
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-font-semibold">Save to...</div>
            <IconButton
              class="tw-shrink-0"
              @click="closeModal(addMentorToListModalName)"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div class="tw-mt-2">
            <FormCheckbox
              v-for="list in lists"
              :key="list.value"
              v-model="mentorAddedToListsIds"
              :value="list.value"
              :is-disabled="listsForm.processing"
              @update:checked="addMentorToList"
              @update:unchecked="removeMentorFromList"
            >
              {{ list.label }}
            </FormCheckbox>
            <div
              v-show="listsForm.processing"
              class="tw-mt-4 tw-flex tw-items-center tw-text-xs tw-text-primary-500"
            >
              <LoadingCircle size="16" class="tw-mr-2" />
              Saving...
            </div>
          </div>
        </div>
        <hr />
        <div class="tw-p-3">
          <button
            class="tw--m-1 tw-inline-flex tw-items-center tw-p-1 tw-text-sm tw-text-primary-500"
            @click="openModal(createNewListModalName)"
          >
            <AddIcon class="tw-mr-2 tw-h-4 tw-w-4" />
            Create new list
          </button>
        </div>
      </div>
    </BaseModal>

    <CreateNewListModal
      :name="createNewListModalName"
      :mentor-id="mentorId"
      @success="handleSuccessCreateList"
    />
  </div>
</template>

<script setup>
import IconButton from '@/shared/components/IconButton.vue'
import FormCheckbox from '@/shared/components/forms/FormCheckbox.vue'
import CloseIcon from '@/assets/images/icons/close.svg'
import AddIcon from '@/assets/images/icons/add.svg'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import CreateNewListModal from '@/shared/components/modals/CreateNewListModal.vue'
import { computed, ref } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { useModals } from '@/shared/composables/useModals.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import BaseModal from '@/shared/components/BaseModal.vue'

const props = defineProps({
  mentorId: {
    type: Number,
    required: true,
  },
  mentorAddedToLists: {
    type: Array,
    default: () => [],
  },
})

const { openModal, closeModal } = useModals()
const page = usePage()
const { isUserLoggedIn } = usePermissionSystem()

const mentorAddedToListsIds = ref(
  props.mentorAddedToLists
    ? props.mentorAddedToLists.map((list) => list.id)
    : [],
)
const listsForm = useForm({
  member: {
    user_id: props.mentorId,
  },
})

const addMentorToListModalName = computed(
  () => `add-mentor-to-list-${props.mentorId}`,
)
const createNewListModalName = computed(
  () => `create-new-list-${props.mentorId}`,
)
const isAddedToAnyList = computed(() => mentorAddedToListsIds.value.length > 0)
const mentorAddedToListsNames = computed(() =>
  mentorAddedToListsIds.value.map(
    (listId) => page.props.lists.find((list) => list.id === listId).name,
  ),
)
const lists = computed(() =>
  page.props.lists.map((list) => ({
    label: list.name,
    value: list.id,
  })),
)

const handleSuccessCreateList = () => {
  // We have to make sure that ids are in sync with the server, because while we create new list we also can add mentor to it.
  mentorAddedToListsIds.value = getMentorAddedToListsIdsState()
}

const addMentorToList = (list) => {
  listsForm.post(`/lists/${list}/members`, {
    preserveScroll: true,
  })
}

const removeMentorFromList = (list) => {
  listsForm.delete(`/lists/${list}/members/${props.mentorId}`, {
    preserveScroll: true,
  })
}

const openAddMentorToListModal = () => {
  openModal(isUserLoggedIn.value ? addMentorToListModalName.value : UNLOCK_FULL_ACCESS_MODAL_NAME)
}

const getMentorAddedToListsIdsState = () => {
  return props.mentorAddedToLists
    ? props.mentorAddedToLists.map((list) => list.id)
    : []
}
</script>
